/**
 * Litchfield Hills inquiry form — tab behaviour, per-panel validation, and
 * ?event= / ?package= deep links.
 *
 * Ported from the birthday-parties app's InquiryForm (Projects/2026/KC&E),
 * minus the parts that only make sense there. That form owns its own state and
 * POSTs JSON to /api/inquiry, so it can validate per event type on the server
 * and reset sibling fields in React. This one is Shopify's native contact form:
 * a plain POST we do not control, with the chosen tab carried by a radio group.
 *
 * So the three jobs here are the ones the platform cannot do on its own:
 *
 *   1. DISABLE the seven inactive panels. Disabled controls are not submitted,
 *      which is what stops a half-filled Corporate panel riding along inside a
 *      Birthday inquiry. It is also what makes (2) safe.
 *   2. MOVE `required` onto the visible panel. The markup ships with none —
 *      a required field inside a display:none panel makes the form
 *      unsubmittable and the browser reports the error against an element
 *      nobody can see. `data-inquiry-required` marks which fields earn it.
 *   3. OPEN the tab named by ?event=, and preselect ?package= for birthdays.
 *
 * Panel VISIBILITY is not our job: master.css §7r keys it off the checked radio
 * with :has(), so the tabs work with this script blocked. What degrades without
 * it is submitted-field hygiene, not the ability to send the form.
 */

const FORM = '[data-inquiry-tabs]';

/* Everything below is scoped to ONE form. The events form and the bike builder
   use the same attributes with different values, and a document-wide query
   would cross-wire them the moment two ever share a page — disabling the other
   form's panels and taking its answers out of the submission. */
const panelsIn = (form) => form.querySelectorAll('[data-event-panel]');

const activeSlug = (form) => {
  const checked = form.querySelector('[data-event]:checked');
  return checked ? checked.dataset.event : null;
};

/* A panel's own controls, never a nested form's. */
const controlsIn = (panel) => panel.querySelectorAll('input, select, textarea');

const syncPanels = (form) => {
  const slug = activeSlug(form);
  panelsIn(form).forEach((panel) => {
    const isActive = panel.dataset.eventPanel === slug;
    panel.hidden = !isActive;
    controlsIn(panel).forEach((el) => {
      el.disabled = !isActive;
      /* required is only ever added to fields the markup nominated, and only
         while their panel is on screen. Removing it on the way out matters as
         much as adding it: a stale required on a disabled field is harmless
         today but becomes a silent block the moment anything re-enables it. */
      if (el.hasAttribute('data-inquiry-required')) el.required = isActive;
    });
  });
};

const applyDeepLink = (form) => {
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }

  /* Matched on the slug in data-event, never the option's text or index: the
     tab copy is meant to be editable and the order reorderable. An unknown or
     stale slug leaves the default tab alone rather than guessing. */
  const slug = params.get('event');
  if (slug) {
    const radio = form.querySelector(`[data-event="${CSS.escape(slug)}"]`);
    if (radio) radio.checked = true;
  }

  const pkg = params.get('package');
  if (pkg) {
    const select = form.querySelector('#rf-package');
    const match = select
      && Array.from(select.options).find((o) => o.dataset.package === pkg);
    if (match) select.value = match.value;
  }
};

/* Honeypot. Shopify runs nothing of ours on submit, so this is the only place
   it can be enforced — see the note in the Liquid for what that does and does
   not buy. Silent rather than an error message: telling a bot why it failed
   only helps it.

   Bound to EVERY .rf-inq form, not just the tabbed one. The bike-build and
   experiences forms (snippets/rifugio-ct-inquiry-form.liquid) carry the same
   decoy field and have no tabs at all, so keying this off the tab list — which
   is what it did when the tabbed form was the only one — left their honeypot
   inert. */
const guardHoneypots = () => {
  document.querySelectorAll('form.rf-inq').forEach((form) => {
    form.addEventListener('submit', (event) => {
      const hp = form.querySelector('.rf-inq__hp');
      if (hp && hp.value.trim() !== '') event.preventDefault();
    });
  });
};

/* Bring the selected tab into view when the strip is scrolled.
   Below about 640px the strip overflows and scrolls — five event tabs need
   520px and a small phone gives it 272px. Without this, arriving on
   ?event=corporate put the active tab off the right-hand edge with the strip
   parked at 0, so the form showed Corporate's fields under a strip that
   appeared to have Birthday selected. Only the strip scrolls; the page does
   not move, which is why this sets scrollLeft rather than calling
   scrollIntoView. */
const revealActiveTab = (tabs, smooth) => {
  if (tabs.scrollWidth <= tabs.clientWidth) return;
  const checked = tabs.querySelector('[data-event]:checked');
  if (!checked) return;
  const label = tabs.querySelector(`label[for="${CSS.escape(checked.id)}"]`);
  if (!label) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  tabs.scrollTo({
    left: Math.max(0, label.offsetLeft - (tabs.clientWidth - label.offsetWidth) / 2),
    behavior: smooth && !reduce ? 'smooth' : 'auto',
  });
};

const init = () => {
  guardHoneypots();

  /* Every tabbed form on the page, not just the first. Today that is the events
     form on the groups page and the bike builder on the bike-shop page, which
     never share one — but the cost of supporting it is a loop. */
  document.querySelectorAll(FORM).forEach((tabs) => {
    const form = tabs.closest('form');
    if (!form) return;

    applyDeepLink(form);
    syncPanels(form);
    revealActiveTab(tabs, false);

    /* Delegated, so the handler survives any future change to the tab list. */
    tabs.addEventListener('change', () => {
      syncPanels(form);
      revealActiveTab(tabs, true);
    });
  });
};

init();
